import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  fetchTypeBasedConnection,
  fetchJiraProjects,
  addProjectKeyToJiraConnection,
} from '../apis/Connections';
import { fetchModulesBySource } from '../apis/EntitiesSelection';
import { ThemeContext } from '../Context/ThemeContext';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const NodeConfigurationPanel = ({
  selectedNode,
  setSelectedNode,
  updateNodeData,
  onConfigUpdate,
}) => {
  const [connectionsList, setConnectionsList] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedModule, setSelectedModule] = useState('');
  const [moduleOptions, setModuleOptions] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');

  const [jiraProjects, setJiraProjects] = useState([]);
  const [loadingJiraProjects, setLoadingJiraProjects] = useState(false);
  const [selectedJiraProject, setSelectedJiraProject] = useState('');
  const [selectedJiraProjectDetails, setSelectedJiraProjectDetails] =
    useState(null);

  const rawLabel =
    selectedNode?.data?.label || selectedNode?.data?.config?.label || '';
  const type = rawLabel.split('(')[0]?.trim()?.toLowerCase();
  const isJiraNode = type === 'jira';
  const isSqlNode = type === 'sql';

  // const getConnectorName = (conn) =>
  //   (conn?.connector_name || conn?.connector || '').toLowerCase();

  // const buildSqlMetadata = (conn) => {
  //   if (!conn) return null;
  //   if (getConnectorName(conn) !== 'sql') return null;
  //   const params = conn?.custom_params || {};
  //   return {
  //     token: params?.token || conn?.token || null,
  //     host: params?.host || '',
  //     port: params?.port ? String(params.port) : '',
  //     database:
  //       params?.database_name || params?.db_name || params?.database || '',
  //   };
  // };
  // Initialize from existing node data if available
  useEffect(() => {
    if (!selectedNode) return;

    // Reset state when switching nodes
    setConnectionsList([]);
    setSelectedConnection(null);
    setSelectedModule('');
    setModuleOptions([]);
    setErrors({});
    setLoadingConnections(true);
    setLoadingModules(true);

    const initialize = async () => {
      if (!type) {
        setLoadingConnections(false);
        setLoadingModules(false);
        return;
      }

      try {
        const connections = await fetchTypeBasedConnection(type);
        setConnectionsList(connections);

        let modules = [];
        if (!isJiraNode) {
          if (isSqlNode) {
            modules = ['SQL'];
          } else {
            modules = await fetchModulesBySource(type);
          }
        }

        setModuleOptions(modules);

        const config = selectedNode?.data?.config;

        if (config?.connection) {
          let matchedConn = connections.find(
            (conn) => conn.conn_id === config.connection.conn_id,
          );

          if (!matchedConn && config.connection.conn_name) {
            matchedConn = connections.find(
              (conn) => conn.conn_name === config.connection.conn_name,
            );
          }

          if (matchedConn) {
            setSelectedConnection(matchedConn || null);
            setSelectedEnvironment(
              typeof matchedConn.environment === 'string'
                ? matchedConn.environment.toLowerCase()
                : '',
            );
          }

          console.log('config:::', config);

          console.log('connections =', connections);
          console.log('matchedConn?.environment =', matchedConn?.environment);
          console.log('selectedEnvironment BEFORE set =', selectedEnvironment);
        }

        if (config?.module && !isJiraNode) {
          setSelectedModule(
            isSqlNode ? config.module?.toUpperCase() : config.module,
          );
        } else if (isSqlNode && !config?.module) {
          setSelectedModule('SQL');
        }
      } catch (error) {
        console.error('❌ Error loading node config:', error);
      } finally {
        setLoadingConnections(false);
        setLoadingModules(false);
      }
    };

    initialize();
  }, [selectedNode]);

  const theme = useTheme();
  const { darkMode } = useContext(ThemeContext);

  const fetchConnections = async () => {
    if (!type) return;
    setLoadingConnections(true);
    const connections = await fetchTypeBasedConnection(type);
    setConnectionsList(connections);
    setLoadingConnections(false);
  };

  if (!selectedNode) return null; // Hide panel when no node is selected

  const fetchModules = async () => {
    if (isJiraNode) return;
    if (!type) return;
    setLoadingModules(true);
    if (isSqlNode) {
      setModuleOptions(['SQL']);
      setLoadingModules(false);
      return;
    }
    const modules = await fetchModulesBySource(type);
    setModuleOptions(modules);
    setLoadingModules(false);
  };

  useEffect(() => {
    const loadProjects = async () => {
      if (!isJiraNode) return; // ✅ guard clause

      setLoadingJiraProjects(true);
      if (selectedConnection?.conn_id) {
        try {
          const data = await fetchJiraProjects(selectedConnection.conn_id);
          setJiraProjects(Array.isArray(data) ? data : []);
        } catch (error) {
          console.error('Error loading Jira projects:', error);
          setJiraProjects([]);
        }
      } else {
        setJiraProjects([]);
      }
      setLoadingJiraProjects(false);
    };

    loadProjects();
  }, [selectedConnection?.conn_id, isJiraNode]);

  // Validation function
  const validateForm = () => {
    let newErrors = {};
    if (!selectedConnection) {
      newErrors.connection = 'Connection is required';
    }

    if (!isJiraNode && !selectedModule) {
      newErrors.module = 'Module is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return; // Stop if validation fails

    // Build label for SQL nodes to match Salesforce format: "SQL (Source)" or "SQL (Destination)"
    let nodeLabel =
      selectedNode?.data?.label || selectedNode?.data?.config?.label;
    if (isSqlNode && !nodeLabel) {
      const typeLabel =
        selectedNode.type === 'sourceNode' || selectedNode.type === 'source'
          ? 'Source'
          : selectedNode.type === 'destinationNode' ||
              selectedNode.type === 'destination'
            ? 'Destination'
            : '';
      nodeLabel = typeLabel ? `SQL (${typeLabel})` : 'SQL';
    }

    // Normalize type to match Salesforce format: "sourceNode" or "destinationNode"
    const normalizedType =
      selectedNode.type === 'source'
        ? 'sourceNode'
        : selectedNode.type === 'destination'
          ? 'destinationNode'
          : selectedNode.type;

    const baseConfig = {
      id: selectedNode.id,
      label: nodeLabel,
      type: normalizedType,
      ...selectedNode?.data?.config,
      connection: selectedConnection
        ? {
            conn_id: selectedConnection.conn_id,
            conn_name: selectedConnection.conn_name,
            client_id: selectedConnection.client_id,
            api_version: selectedConnection.api_version,
            connector_name:
              selectedConnection.connector_name ||
              selectedConnection.connector ||
              type?.toUpperCase() ||
              '',
            environment: selectedConnection.environment,
          }
        : null,
    };

    // ✅ Conditionally include module if not Jira (SQL and GitHub should send lowercase)
    if (!isJiraNode) {
      const isGitHubNode = type === 'github';
      const normalizedModule = isSqlNode
        ? (selectedModule || 'SQL').toLowerCase()
        : isGitHubNode
          ? (selectedModule || '').toLowerCase()
          : selectedModule;
      baseConfig.module = normalizedModule;
    }

    if (isJiraNode && selectedJiraProjectDetails) {
      baseConfig.project = {
        key: selectedJiraProjectDetails.key,
        name: selectedJiraProjectDetails.name,
        id: selectedJiraProjectDetails.id,
      };
    }

    console.log('Saving config:', baseConfig);

    // Actually update the node data
    updateNodeData(selectedNode.id, baseConfig);
    onConfigUpdate(baseConfig);

    setSelectedNode(null); // Close the panel after saving
  };

  const getContainedButtonStyle = () => ({
    fontSize: '0.875rem',
    backgroundColor: '#5A66A6',
    padding: '0.3rem 0.8rem',
    color: '#ffffff',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    borderRadius: 0.5,
    boxShadow: '0px 4px 10px 0px #0000000D',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.03)',
      backgroundColor: '#1976D2',
      boxShadow: 'linear-gradient(90deg, #2f7fd6, #0097d6)',
      border: '1px solid transparent',
    },
    '&:active': {
      backgroundColor: '#1565C0',
      boxShadow: 'linear-gradient(90deg, #2f7fd6, #0097d6)',
      border: '1px solid transparent',
    },
  });

  // Common dropdown styling to ensure equal width regardless of content
  const getDropdownStyle = (hasError = false) => ({
    mb: 2,
    borderRadius: '6px',
    backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
    height: '2.5rem',
    width: '100%',
    color: darkMode ? '#ffffff' : theme.palette.text.primary,
    transition: 'all 0.2s ease-in-out',
    '.MuiSelect-select': {
      color: darkMode ? '#ffffff' : theme.palette.text.primary,
      padding: '12px 16px',
      fontSize: '0.95rem',
      width: '100%', // Ensure select content takes full width
      textOverflow: 'ellipsis', // Handle long text gracefully
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : darkMode ? '#404040' : '#DDDFE3',
      borderWidth: '1px',
      borderRadius: '6px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : darkMode ? '#2196F3' : '#2196F3',
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : '#2196F3',
      borderWidth: '1px',
      boxShadow: hasError
        ? '0 0 0 3px rgba(244, 67, 54, 0.1)'
        : darkMode
          ? '0 0 0 3px rgba(33, 150, 243, 0.1)'
          : '0 0 0 3px rgba(33, 150, 243, 0.1)',
    },
    '& .MuiSvgIcon-root': {
      color: darkMode ? '#ffffff' : '#666666',
      transition: 'transform 0.2s ease-in-out',
    },
    '&:hover .MuiSvgIcon-root': {
      color: '#2196F3',
    },
    boxShadow: darkMode
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.08)',
  });

  // Common menu props for consistent dropdown behavior
  const getMenuProps = () => ({
    PaperProps: {
      sx: {
        backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
        color: darkMode ? '#ffffff' : theme.palette.text.primary,
        boxShadow: darkMode
          ? '0 8px 32px rgba(0, 0, 0, 0.4)'
          : '0 8px 32px rgba(0, 0, 0, 0.12)',
        mt: 1,
        borderRadius: '12px',
        border: darkMode ? '1px solid #404040' : '1px solid #e0e0e0',
        maxHeight: '300px',
        minWidth: '300px', // Ensure dropdown menu has consistent width
        '& .MuiMenuItem-root': {
          mx: 1,
          my: 0.5,
          borderRadius: '8px',
          fontSize: '0.95rem',
          fontWeight: 400,
          padding: '12px 16px',
          transition: 'all 0.15s ease-in-out',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          '&.Mui-selected': {
            backgroundColor: darkMode ? '#1976D2' : '#e3f2fd',
            color: darkMode ? '#ffffff' : '#1976D2',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: darkMode ? '#1565C0' : '#bbdefb',
            },
          },
          '&:hover': {
            backgroundColor: darkMode ? '#404040' : '#f5f5f5',
            transform: 'translateX(4px)',
          },
          '&.Mui-disabled': {
            color: darkMode ? '#666666' : '#999999',
            opacity: 0.6,
          },
        },
      },
    },
  });

  return (
    <Box
      sx={{
        maxWidth: '600px',
        // height: '95%',
        backgroundColor: darkMode ? '#1e1e1e' : theme.palette.background.paper,
        // boxShadow: darkMode
        //   ? '-5px 0 10px rgba(0, 0, 0, 0.5)'
        //   : '-5px 0 10px rgba(0, 0, 0, 0.2)',
        // borderLeft: darkMode
        //   ? '1px solid #333333'
        //   : `1px solid ${theme.palette.divider}`,
        padding: '1.5rem 2rem',
        display: 'flex',
        flexDirection: 'column',
        // transition: 'width 0.3s ease-in-out',
        color: darkMode ? '#ffffff' : theme.palette.text.primary,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontSize: 19 }}
          color={darkMode ? '#ffffff' : 'text.primary'}
        >
          Set Properties
        </Typography>
      </Box>

      <Typography
        variant="body1"
        sx={{ mb: 1, fontWeight: 500 }}
        color={darkMode ? '#ffffff' : 'text.primary'}
      >
        Select Environment
      </Typography>
      <Select
        fullWidth
        value={
          ['test', 'production'].includes(selectedEnvironment)
            ? selectedEnvironment
            : ''
        }
        onChange={(e) => {
          setSelectedEnvironment(e.target.value);
          setSelectedConnection(null);
        }}
        displayEmpty
        sx={getDropdownStyle()}
        MenuProps={getMenuProps()}
        IconComponent={KeyboardArrowDownIcon}
      >
        <MenuItem value="" disabled>
          Select Environment
        </MenuItem>

        <MenuItem value="test">Test</MenuItem>
        <MenuItem value="production">Production</MenuItem>
      </Select>

      <Typography
        variant="body1"
        sx={{ mb: 1, fontWeight: 500 }}
        color={darkMode ? '#ffffff' : 'text.primary'}
      >
        Select Connection
      </Typography>
      <Select
        fullWidth
        value={selectedConnection?.conn_id || ''}
        onOpen={fetchConnections}
        onChange={(e) => {
          const selected = connectionsList.find(
            (conn) => conn.conn_id === e.target.value,
          );
          if (!selected) return;
          setSelectedConnection(selected);

          // Build label for SQL nodes to match Salesforce format
          let nodeLabel =
            selectedNode?.data?.label || selectedNode?.data?.config?.label;
          if (isSqlNode && !nodeLabel) {
            const typeLabel =
              selectedNode.type === 'sourceNode' ||
              selectedNode.type === 'source'
                ? 'Source'
                : selectedNode.type === 'destinationNode' ||
                    selectedNode.type === 'destination'
                  ? 'Destination'
                  : '';
            nodeLabel = typeLabel ? `SQL (${typeLabel})` : 'SQL';
          }

          // Normalize type to match Salesforce format
          const normalizedType =
            selectedNode.type === 'source'
              ? 'sourceNode'
              : selectedNode.type === 'destination'
                ? 'destinationNode'
                : selectedNode.type;

          // Update node data with complete config structure matching Salesforce format
          const updatedConfig = {
            id: selectedNode.id,
            label: nodeLabel,
            type: normalizedType,
            ...selectedNode?.data?.config,
            connection: {
              conn_id: selected.conn_id,
              conn_name: selected.conn_name,
              client_id: selected.client_id,
              api_version: selected.api_version,
              connector_name:
                selected.connector_name ||
                selected.connector ||
                type?.toUpperCase() ||
                '',
              environment: selected.environment,
            },
            // Set module for SQL nodes
            ...(isSqlNode && { module: 'sql' }),
          };

          updateNodeData(selectedNode.id, updatedConfig);
          onConfigUpdate(updatedConfig);
        }}
        displayEmpty
        sx={getDropdownStyle(Boolean(errors.connection))}
        error={Boolean(errors.connection)}
        MenuProps={getMenuProps()}
        IconComponent={KeyboardArrowDownIcon}
      >
        <MenuItem value="" disabled>
          {loadingConnections ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
            </Box>
          ) : (
            'Select'
          )}
        </MenuItem>

        {!loadingConnections &&
          connectionsList
            .filter((conn) =>
              selectedEnvironment === 'production'
                ? !conn.environment ||
                  conn.environment.toLowerCase() === 'production'
                : conn.environment?.toLowerCase() ===
                  selectedEnvironment?.toLowerCase(),
            )
            .map((conn) => (
              <MenuItem key={conn.conn_id} value={conn.conn_id}>
                {conn.conn_name}
              </MenuItem>
            ))}
      </Select>

      {errors.connection && (
        <Typography variant="caption" color="error">
          {errors.connection}
        </Typography>
      )}

      {!isJiraNode && (
        <>
          <Typography
            variant="body1"
            sx={{ mb: 1, fontWeight: 500 }}
            color={darkMode ? '#ffffff' : 'text.primary'}
          >
            Select Module
          </Typography>
          <Select
            fullWidth
            value={selectedModule || ''}
            onChange={(e) => {
              const selected = e.target.value;
              setSelectedModule(selected);
              const normalizedModule = isSqlNode
                ? (selected || 'SQL').toLowerCase()
                : selected;

              // Build label for SQL nodes to match Salesforce format
              let nodeLabel =
                selectedNode?.data?.label || selectedNode?.data?.config?.label;
              if (isSqlNode && !nodeLabel) {
                const typeLabel =
                  selectedNode.type === 'sourceNode' ||
                  selectedNode.type === 'source'
                    ? 'Source'
                    : selectedNode.type === 'destinationNode' ||
                        selectedNode.type === 'destination'
                      ? 'Destination'
                      : '';
                nodeLabel = typeLabel ? `SQL (${typeLabel})` : 'SQL';
              }

              // Normalize type to match Salesforce format
              const normalizedType =
                selectedNode.type === 'source'
                  ? 'sourceNode'
                  : selectedNode.type === 'destination'
                    ? 'destinationNode'
                    : selectedNode.type;

              const updatedConfig = {
                id: selectedNode.id,
                label: nodeLabel,
                type: normalizedType,
                ...selectedNode?.data?.config,
                connection: selectedConnection
                  ? {
                      conn_id: selectedConnection.conn_id,
                      conn_name: selectedConnection.conn_name,
                      client_id: selectedConnection.client_id,
                      api_version: selectedConnection.api_version,
                      connector_name:
                        selectedConnection.connector_name ||
                        selectedConnection.connector ||
                        type?.toUpperCase() ||
                        '',
                      environment: selectedConnection.environment,
                    }
                  : null,
                module: normalizedModule,
              };

              console.log(
                '✅ Module Updated Config JSON:',
                JSON.stringify(updatedConfig, null, 2),
              );
              updateNodeData(selectedNode.id, updatedConfig);
              onConfigUpdate(updatedConfig);
            }}
            onOpen={fetchModules}
            displayEmpty
            sx={{ ...getDropdownStyle(), mb: 3 }}
            MenuProps={getMenuProps()}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="" disabled>
              {loadingModules ? (
                <Box display="flex" alignItems="center">
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                </Box>
              ) : (
                'Select'
              )}
            </MenuItem>
            {!loadingModules &&
              Array.isArray(moduleOptions) &&
              moduleOptions.map((module) => (
                <MenuItem key={module} value={module}>
                  {module}
                </MenuItem>
              ))}
          </Select>
          {errors.module && (
            <Typography variant="caption" color="error" sx={{ mb: 2 }}>
              {errors.module}
            </Typography>
          )}
        </>
      )}

      {isJiraNode && (
        <>
          <Typography
            variant="body1"
            sx={{ mb: 1, fontWeight: 500 }}
            color={darkMode ? '#ffffff' : 'text.primary'}
          >
            Select Jira Project
          </Typography>

          <Select
            fullWidth
            value={selectedJiraProject}
            onChange={async (e) => {
              const projectKey = e.target.value;
              setSelectedJiraProject(projectKey);

              // Correctly find the selected project from loaded projects
              const selectedProjectObj = jiraProjects.find(
                (p) => p.key === projectKey || p.id === projectKey, // fallback if you pass ID in <MenuItem>
              );

              console.log('selectedProjectObj', selectedProjectObj);
              setSelectedJiraProjectDetails(selectedProjectObj); // still update for persistence

              // ✅ Use selectedProjectObj directly instead of selectedJiraProjectDetails
              if (selectedProjectObj) {
                const updatedConfig = {
                  id: selectedNode.id,
                  label:
                    selectedNode?.data?.label ||
                    selectedNode?.data?.config?.label,
                  type: selectedNode.type,
                  ...selectedNode?.data?.config,
                  connection: selectedConnection
                    ? {
                        conn_id: selectedConnection.conn_id,
                        conn_name: selectedConnection.conn_name,
                        client_id: selectedConnection.client_id,
                        api_version: selectedConnection.api_version,
                      }
                    : null,
                  project: {
                    key: selectedProjectObj.key,
                    name: selectedProjectObj.name,
                    id: selectedProjectObj.id,
                  },
                };

                updateNodeData(selectedNode.id, updatedConfig);
                onConfigUpdate(updatedConfig);
                console.log(
                  '✅ Updated node config with Jira project',
                  updatedConfig,
                );
              } else {
                console.warn(
                  '⚠️ Project not found in list for key:',
                  projectKey,
                );
              }

              try {
                await addProjectKeyToJiraConnection(
                  selectedConnection?.conn_id,
                  projectKey,
                );
                console.log(
                  '✅ Project key added to Jira connection successfully',
                );
              } catch (error) {
                console.error('❌ Failed to add project key:', error.message);
              }
            }}
            displayEmpty
            sx={{ ...getDropdownStyle(darkMode, theme), mb: 3 }}
            MenuProps={getMenuProps(darkMode, theme)}
          >
            <MenuItem value="" disabled>
              {loadingJiraProjects ? (
                <Box display="flex" alignItems="center">
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                </Box>
              ) : (
                'Select'
              )}
            </MenuItem>

            {!loadingJiraProjects &&
              jiraProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
          </Select>
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          // mt: 'auto',
          // mb: 12,
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => setSelectedNode(null)}
          sx={{
            borderRadius: '0.5rem',
            padding: '0.3rem 0.8rem',
            fontSize: '0.875rem',
            // borderColor: 'rgba(79, 172, 254, 0.59)',
            color: '#5A66A6',
            border: 'none',
            background: 'transparent',
            '&:hover': {
              // borderColor: 'rgba(79, 172, 254, 0.8)',
              border: '1px solid #5A66A6',
              backgroundColor: darkMode
                ? 'rgba(79, 172, 254, 0.1)'
                : 'rgba(79, 172, 254, 0.05)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          sx={getContainedButtonStyle(theme)}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

NodeConfigurationPanel.propTypes = {
  selectedNode: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    data: PropTypes.shape({
      label: PropTypes.string.isRequired,
      config: PropTypes.object, // Allow dynamic config shape
    }).isRequired,
  }),
  setSelectedNode: PropTypes.func.isRequired,
  updateNodeData: PropTypes.func.isRequired,
  onConfigUpdate: PropTypes.func.isRequired,
};

export default NodeConfigurationPanel;
